import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { ClipboardList, Loader2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCreateProduct } from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useFormField,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { kickoffProductPdfSourceUploads } from '@/features/product-catalog/lib/kickoffProductPdfSourceUploads'
import {
  assessProductSourceUploadEligibilityBatch,
  type ProductSourceUploadEligibility,
} from '@/features/product-catalog/lib/assessProductSourceUploadEligibility'
import { ProductSourceUploadReviewPanel } from '@/features/product-catalog/components/ProductSourceUploadReviewPanel'
import { ProductSourceUploadDialogInstructions } from '@/features/product-catalog/components/ProductSourceUploadDialogInstructions'
import {
  isAllowedProductSourceFile,
  PRODUCT_SOURCE_FILE_ACCEPT,
} from '@/features/product-catalog/utils/productSourceFileTypes'
import {
  addProductFormResolver,
  PRODUCT_FORM_VALIDATION_I18N_KEYS,
  type AddProductFormValues,
  type ProductFormErrorCode,
} from './Product.schema'
import {
  NO_MANUFACTURER_VALUE,
  ProductManufacturerSelect,
} from './ProductManufacturerSelect'

function AddProductFormMessage() {
  const { error, formMessageId } = useFormField()
  const { t } = useTranslation('productCatalog')
  if (!error?.message) {
    return null
  }
  const code = error.message as ProductFormErrorCode
  const i18nKey = PRODUCT_FORM_VALIDATION_I18N_KEYS[code]
  const body = i18nKey != null ? t(i18nKey) : error.message
  return (
    <p
      data-slot='form-message'
      id={formMessageId}
      className='text-sm text-destructive'
    >
      {body}
    </p>
  )
}

type AddProductDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (productId: string) => void
}

export function AddProductDialog({
  open,
  onOpenChange,
  onCreated,
}: AddProductDialogProps): ReactElement {
  const { t } = useTranslation('productCatalog')
  const queryClient = useQueryClient()
  const { mutateAsync: createProduct, isPending: isManualSubmitting } =
    useCreateProduct()

  const form = useForm<AddProductFormValues>({
    resolver: addProductFormResolver,
    defaultValues: {
      name: '',
      description: '',
      associatedPartyId: NO_MANUFACTURER_VALUE,
      yearOfManufacture: '',
      productModel: '',
    },
  })

  const [activeTab, setActiveTab] = useState<'manual' | 'documents'>(
    'documents'
  )
  const [documentsPhase, setDocumentsPhase] = useState<'pick' | 'review'>('pick')
  const [isAssessingDocuments, setIsAssessingDocuments] = useState(false)
  const [documentAssessments, setDocumentAssessments] = useState<
    ProductSourceUploadEligibility[]
  >(() => [])
  const [isCreatingFromDocuments, setIsCreatingFromDocuments] = useState(false)

  const documentsInputRef = useRef<HTMLInputElement>(null)

  const isBusy = isManualSubmitting || isCreatingFromDocuments

  useEffect(() => {
    if (!open) {
      form.reset({
        name: '',
        description: '',
        associatedPartyId: NO_MANUFACTURER_VALUE,
        yearOfManufacture: '',
        productModel: '',
      })
      setActiveTab('documents')
      setDocumentsPhase('pick')
      setIsAssessingDocuments(false)
      setDocumentAssessments([])
    }
  }, [open, form])

  const eligibleDocumentCount = documentAssessments.filter(
    (row) => row.eligible
  ).length

  async function onManualSubmit(values: AddProductFormValues) {
    try {
      const year = parseInt(values.yearOfManufacture, 10)
      const productModel =
        values.productModel.trim() === ''
          ? undefined
          : values.productModel.trim()
      let associatedPartyId: string | undefined
      if (values.associatedPartyId !== NO_MANUFACTURER_VALUE) {
        associatedPartyId = values.associatedPartyId
      }
      const product = await createProduct({
        name: values.name,
        description: values.description,
        ...(associatedPartyId != null ? { associatedPartyId } : {}),
        yearOfManufacture: year,
        ...(productModel != null ? { productModel } : {}),
      })
      onOpenChange(false)
      onCreated(product.id)
    } catch {
      toast.error(t('dialog.createError'))
    }
  }

  const assessAndReviewDocuments = useCallback(
    async (incoming: File[]) => {
      if (incoming.length === 0) {
        return
      }
      const hasAllowedFile = incoming.some((file) =>
        isAllowedProductSourceFile(file)
      )
      if (!hasAllowedFile) {
        toast.error(t('sources.addDialog.allowedFileTypes'))
        return
      }
      setDocumentsPhase('review')
      setIsAssessingDocuments(true)
      try {
        const nextAssessments =
          await assessProductSourceUploadEligibilityBatch(incoming)
        setDocumentAssessments(nextAssessments)
      } finally {
        setIsAssessingDocuments(false)
      }
    },
    [t]
  )

  const handleDocumentsInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const list = event.target.files
      if (list && list.length > 0) {
        void assessAndReviewDocuments(Array.from(list))
      }
      event.target.value = ''
    },
    [assessAndReviewDocuments]
  )

  const handleDocumentsDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const list = event.dataTransfer.files
      if (list && list.length > 0) {
        void assessAndReviewDocuments(Array.from(list))
      }
    },
    [assessAndReviewDocuments]
  )

  async function onFromDocumentsSubmit() {
    const eligibleFiles = documentAssessments
      .filter((row) => row.eligible)
      .map((row) => row.file)
    if (eligibleFiles.length === 0) {
      toast.error(t('dialog.fromDocuments.validation.filesRequired'))
      return
    }
    setIsCreatingFromDocuments(true)
    try {
      const product = await createProduct({})
      void kickoffProductPdfSourceUploads({
        productId: product.id,
        files: eligibleFiles,
        queryClient,
        translate: t,
      })
      onOpenChange(false)
      onCreated(product.id)
    } catch {
      toast.error(t('dialog.createError'))
    } finally {
      setIsCreatingFromDocuments(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
          {activeTab === 'documents' ? (
            <ProductSourceUploadDialogInstructions
              variant={documentsPhase === 'review' ? 'review' : 'pick'}
              context='fromDocuments'
            />
          ) : (
            <DialogDescription>{t('dialog.description')}</DialogDescription>
          )}
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value as 'manual' | 'documents')
          }}
          className='w-full'
        >
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='documents' disabled={isBusy} className='gap-2'>
              <Upload className='size-4 shrink-0' aria-hidden />
              {t('dialog.tabs.fromDocuments')}
            </TabsTrigger>
            <TabsTrigger value='manual' disabled={isBusy} className='gap-2'>
              <ClipboardList className='size-4 shrink-0' aria-hidden />
              {t('dialog.tabs.manual')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value='documents' className='space-y-4 pt-4'>
            {documentsPhase === 'review' && (
              <ProductSourceUploadReviewPanel
                assessments={documentAssessments}
                isAssessing={isAssessingDocuments}
                isUploading={isCreatingFromDocuments}
                hideActions
                onChooseDifferentFiles={() => {
                  setDocumentsPhase('pick')
                  setDocumentAssessments([])
                }}
                onConfirmUpload={() => {
                  void onFromDocumentsSubmit()
                }}
              />
            )}
            {documentsPhase === 'pick' && (
              <div
                className={cn(
                  'flex min-h-[160px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center',
                  'border-muted-foreground/25 hover:border-muted-foreground/50',
                  isBusy && 'pointer-events-none opacity-70'
                )}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => event.preventDefault()}
                onDrop={handleDocumentsDrop}
              >
                <Upload className='mb-2 size-8 text-muted-foreground' />
                <p className='mb-1 text-sm text-muted-foreground'>
                  {t('sources.addDialog.dragDrop')}
                </p>
                <p className='mb-3 text-xs text-muted-foreground'>
                  {t('sources.addDialog.orClick')}
                </p>
                <input
                  ref={documentsInputRef}
                  type='file'
                  accept={PRODUCT_SOURCE_FILE_ACCEPT}
                  multiple
                  className='sr-only'
                  onChange={handleDocumentsInputChange}
                  disabled={isBusy}
                />
                <Button
                  disabled={isBusy}
                  variant='secondary'
                  size='sm'
                  type='button'
                  onClick={() => documentsInputRef.current?.click()}
                >
                  {t('sources.addDialog.selectPdf')}
                </Button>
              </div>
            )}

            {documentsPhase === 'review' && (
              <DialogFooter className='flex flex-col-reverse gap-4 sm:flex-row sm:justify-end sm:gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setDocumentsPhase('pick')
                    setDocumentAssessments([])
                  }}
                  disabled={isBusy}
                >
                  {t('sources.addDialog.review.chooseDifferentFiles')}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => onOpenChange(false)}
                  disabled={isBusy}
                >
                  {t('dialog.cancel')}
                </Button>
                <Button
                  type='button'
                  disabled={
                    isBusy ||
                    isAssessingDocuments ||
                    eligibleDocumentCount === 0
                  }
                  onClick={() => {
                    void onFromDocumentsSubmit()
                  }}
                >
                  {isCreatingFromDocuments ? (
                    <>
                      <Loader2 className='me-2 size-4 animate-spin' />
                      {t('dialog.submitting')}
                    </>
                  ) : (
                    t('dialog.fromDocuments.submitEligible', {
                      count: eligibleDocumentCount,
                    })
                  )}
                </Button>
              </DialogFooter>
            )}
            {documentsPhase === 'pick' && (
              <DialogFooter className='flex flex-col-reverse gap-4 sm:flex-row sm:justify-end sm:gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => onOpenChange(false)}
                  disabled={isBusy}
                >
                  {t('dialog.cancel')}
                </Button>
              </DialogFooter>
            )}
          </TabsContent>

          <TabsContent value='manual' className='space-y-4 pt-4'>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onManualSubmit)}
                className='space-y-4'
              >
                <FormField
                  control={form.control}
                  name='name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('dialog.fields.name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('dialog.fields.namePlaceholder')}
                          autoComplete='off'
                          disabled={isBusy}
                          {...field}
                        />
                      </FormControl>
                      <AddProductFormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='description'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('dialog.fields.description')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t(
                            'dialog.fields.descriptionPlaceholder'
                          )}
                          rows={3}
                          disabled={isBusy}
                          {...field}
                        />
                      </FormControl>
                      <AddProductFormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='associatedPartyId'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('dialog.fields.manufacturer')}</FormLabel>
                      <ProductManufacturerSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isBusy}
                        enabled={open && activeTab === 'manual'}
                        includeNoManufacturerOption
                      />
                      <AddProductFormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='yearOfManufacture'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('dialog.fields.yearOfManufacture')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          inputMode='numeric'
                          pattern='[0-9]*'
                          placeholder={t('dialog.fields.yearPlaceholder')}
                          autoComplete='off'
                          disabled={isBusy}
                          {...field}
                        />
                      </FormControl>
                      <AddProductFormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='productModel'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('dialog.fields.productModel')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            'dialog.fields.productModelPlaceholder'
                          )}
                          autoComplete='off'
                          disabled={isBusy}
                          {...field}
                        />
                      </FormControl>
                      <AddProductFormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter className='flex flex-col-reverse gap-4 sm:flex-row sm:justify-end sm:gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => onOpenChange(false)}
                    disabled={isBusy}
                  >
                    {t('dialog.cancel')}
                  </Button>
                  <Button type='submit' disabled={isBusy}>
                    {isManualSubmitting ? (
                      <>
                        <Loader2 className='me-2 size-4 animate-spin' />
                        {t('dialog.submitting')}
                      </>
                    ) : (
                      t('dialog.submit')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
